import React from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import { Close, WarningAmberRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { LOBBY_EXIT_BUTTON_SX, DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import { DELETE_CONFIRMATION_TEXT } from "./accountDataDeletionUtils";
import {
    ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_ERROR_SX,
    ACCOUNT_PAGE_FIELD_LABEL_SX,
} from "./accountPageStyles";

const DIALOG_FADE_IN = {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
};

const FIELD_SX = {
    ...OUTLINED_INPUT_SX,
    "& .MuiFormHelperText-root": {
        color: colors.text.muted,
        mx: 0,
        mt: "0.4rem",
        fontSize: "0.75rem",
    },
};

const ACTION_BUTTON_HEIGHT = "2.5rem";
const ACTION_BUTTON_PX = "1.25rem";
const ACTION_BUTTON_LAYOUT_SX = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    appearance: "none",
    WebkitAppearance: "none",
    m: 0,
    height: ACTION_BUTTON_HEIGHT,
    padding: `0 ${ACTION_BUTTON_PX}`,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    lineHeight: 1,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    [`@media ${MOBILE_NAV_MEDIA}`]: {
        padding: `0 ${ACTION_BUTTON_PX}`,
        fontSize: "0.72rem",
        letterSpacing: "0.08em",
        lineHeight: 1,
    },
};

const SUBMIT_BUTTON_SX = {
    ...LOBBY_EXIT_BUTTON_SX,
    ...ACTION_BUTTON_LAYOUT_SX,
    flex: "0 0 auto",
    width: "auto",
    minWidth: "11.25rem",
    "&:disabled": {
        background: "rgba(255,255,255,0.06)",
        color: "rgba(255,255,255,0.2)",
        border: "none",
        boxShadow: "none",
        filter: "none",
        transform: "none",
        cursor: "not-allowed",
        "&::before": {
            opacity: 0,
        },
        "&::after": {
            opacity: 0,
        },
    },
    "&:disabled:hover": {
        background: "rgba(255,255,255,0.06)",
        boxShadow: "none",
        filter: "none",
        transform: "none",
    },
};

const CANCEL_BUTTON_SX = {
    ...ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX,
    ...ACTION_BUTTON_LAYOUT_SX,
    flex: "0 0 auto",
    width: "auto",
    minWidth: "8.5rem",
    borderRadius: "8px",
};

export default function AccountDeletitionDialog({
    open,
    onClose,
    onSubmit,
    submitting = false,
    form,
    onFormChange,
    twoFactorEnabled = false,
    error = null,
    canSubmit = false,
}) {
    if (!open) return null;

    return (
        <BackdropOverlay open onClick={submitting ? undefined : onClose}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-labelledby="account-deletion-dialog-title"
                sx={{
                    "@keyframes dialogFadeIn": DIALOG_FADE_IN,
                    position: "relative",
                    width: { xs: "100%", sm: "27rem" },
                    maxWidth: "27rem",
                    overflow: "hidden",
                    background: `
                        linear-gradient(
                            165deg,
                            rgba(18,14,14,0.98) 0%,
                            rgba(10,10,12,0.99) 48%,
                            rgba(8,8,10,1) 100%
                        )
                    `,
                    borderRadius: DIALOG_PAPER_RADIUS,
                    border: `1px solid ${colors.border.default}`,
                    boxShadow: "0 28px 64px rgba(0, 0, 0, 0.55)",
                    animation: "dialogFadeIn 0.15s ease-in-out",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "12%",
                        right: "12%",
                        height: "1px",
                        background: `linear-gradient(90deg, transparent, ${alpha(colors.state.danger.main, 0.55)}, transparent)`,
                        pointerEvents: "none",
                    },
                }}
            >
                <IconButton
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close deletion dialog"
                    sx={{
                        position: "absolute",
                        top: "0.85rem",
                        right: "0.85rem",
                        width: "1.9rem",
                        height: "1.9rem",
                        borderRadius: "0.55rem",
                        color: colors.text.secondary,
                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            color: colors.text.primary,
                        },
                    }}
                >
                    <Close sx={{ fontSize: "0.95rem" }} />
                </IconButton>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        px: { xs: "1.35rem", sm: "1.6rem" },
                        pt: "1.55rem",
                        pb: "1.4rem",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            pr: "2.5rem",
                            mb: "0.75rem",
                        }}
                    >
                        <Box
                            sx={{
                                width: "2.35rem",
                                height: "2.35rem",
                                flexShrink: 0,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `radial-gradient(circle at top, ${alpha(colors.state.danger.main, 0.24)}, ${alpha(colors.state.danger.main, 0.045)})`,
                                border: `1px solid ${alpha(colors.state.danger.main, 0.24)}`,
                            }}
                        >
                            <WarningAmberRounded
                                sx={{
                                    fontSize: "1.1rem",
                                    color: colors.state.danger.main,
                                }}
                            />
                        </Box>
                        <Typography
                            id="account-deletion-dialog-title"
                            component="h2"
                            sx={{
                                color: colors.text.primary,
                                fontSize: "1rem",
                                fontWeight: 800,
                                letterSpacing: "0.07em",
                                textTransform: "uppercase",
                                lineHeight: 1.25,
                            }}
                        >
                            Delete your account?
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            mt: 0,
                            color: colors.text.secondary,
                            fontSize: "0.84rem",
                            fontWeight: 500,
                            lineHeight: 1.6,
                        }}
                    >
                        Your account and personal data will be permanently deleted.
                        This cannot be undone.
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.45rem",
                            color: colors.text.muted,
                            fontSize: "0.74rem",
                            fontWeight: 500,
                            lineHeight: 1.5,
                        }}
                    >
                        Minimal records may remain for compliance and misuse prevention.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.9rem",
                            mt: "1.05rem",
                        }}
                    >
                        <Box>
                            <Typography
                                component="label"
                                htmlFor="account-deletion-confirmation"
                                sx={{
                                    ...ACCOUNT_PAGE_FIELD_LABEL_SX,
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "0.32rem",
                                }}
                            >
                                Type
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-block",
                                        px: "0.4rem",
                                        py: "0.08rem",
                                        borderRadius: "4px",
                                        background: "rgba(255,255,255,0.05)",
                                        border: `1px solid ${colors.border.default}`,
                                        color: colors.text.primary,
                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        fontSize: "0.72rem",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    {DELETE_CONFIRMATION_TEXT}
                                </Box>
                                to continue
                            </Typography>
                            <TextField
                                id="account-deletion-confirmation"
                                fullWidth
                                name="confirmationText"
                                value={form.confirmationText}
                                onChange={onFormChange}
                                placeholder={DELETE_CONFIRMATION_TEXT}
                                sx={{ ...FIELD_SX, mt: "0.55rem" }}
                                slotProps={{
                                    htmlInput: {
                                        autoComplete: "off",
                                        autoCorrect: "off",
                                        spellCheck: false,
                                    },
                                }}
                            />
                        </Box>

                        {twoFactorEnabled ? (
                            <Box>
                                <Typography
                                    component="label"
                                    htmlFor="account-deletion-2fa"
                                    sx={ACCOUNT_PAGE_FIELD_LABEL_SX}
                                >
                                    Authenticator code
                                </Typography>
                                <TextField
                                    id="account-deletion-2fa"
                                    fullWidth
                                    name="twoFactorCode"
                                    value={form.twoFactorCode}
                                    onChange={onFormChange}
                                    placeholder="6-digit code"
                                    helperText="Required before your account can be deleted."
                                    sx={{ ...FIELD_SX, mt: "0.45rem" }}
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: 6,
                                            inputMode: "numeric",
                                            autoComplete: "one-time-code",
                                        },
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>

                    {error ? (
                        <Typography sx={{ ...ACCOUNT_PAGE_FIELD_ERROR_SX, mt: "0.9rem" }}>
                            {error}
                        </Typography>
                    ) : null}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            gap: "0.65rem",
                            mt: "1.35rem",
                            [`@media ${MOBILE_NAV_MEDIA}`]: {
                                flexDirection: "column",
                            },
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            disabled={submitting}
                            onClick={onClose}
                            sx={{
                                ...CANCEL_BUTTON_SX,
                                [`@media ${MOBILE_NAV_MEDIA}`]: {
                                    order: 0,
                                    width: "100%",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            Cancel
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            disabled={!canSubmit || submitting}
                            onClick={onSubmit}
                            sx={{
                                ...SUBMIT_BUTTON_SX,
                                [`@media ${MOBILE_NAV_MEDIA}`]: {
                                    order: 1,
                                    width: "100%",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            {submitting ? (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: colors.text.primary }}
                                />
                            ) : (
                                "Delete account"
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

AccountDeletitionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    form: PropTypes.shape({
        confirmationText: PropTypes.string,
        twoFactorCode: PropTypes.string,
    }).isRequired,
    onFormChange: PropTypes.func.isRequired,
    twoFactorEnabled: PropTypes.bool,
    error: PropTypes.string,
    canSubmit: PropTypes.bool,
};
